
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ArticleCard from '@/components/ui/ArticleCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { featuredAchievers, regularAchievers } from '@/data/articles';

const SearchResults: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([...featuredAchievers, ...regularAchievers]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ q: searchQuery });
  };

  // Perform search when query param changes
  useEffect(() => {
    setIsSearching(true);
    
    // Simulate a small delay for search to feel more realistic
    const timer = setTimeout(() => {
      if (initialQuery) {
        const allAchievers = [...featuredAchievers, ...regularAchievers];
        const results = allAchievers.filter((achiever) => {
          const searchTerm = initialQuery.toLowerCase();
          return (
            achiever.title.toLowerCase().includes(searchTerm) ||
            achiever.excerpt.toLowerCase().includes(searchTerm) ||
            achiever.category.toLowerCase().includes(searchTerm) ||
            achiever.author.name.toLowerCase().includes(searchTerm)
          );
        });
        setSearchResults(results);
      } else {
        setSearchResults([...featuredAchievers, ...regularAchievers]);
      }
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [initialQuery]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-10 px-4 md:px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto">
          {/* Search Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-6">Search Results</h1>
            
            <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search achievers..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={searchQuery.trim() === ''}>Search</Button>
            </form>
          </div>
          
          {/* Search Results */}
          <div>
            {isSearching ? (
              <div className="flex justify-center py-10">
                <div className="animate-pulse text-muted-foreground">Searching...</div>
              </div>
            ) : initialQuery ? (
              <>
                <div className="mb-6 text-muted-foreground">
                  Found {searchResults.length} results for "{initialQuery}"
                </div>
                
                {searchResults.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {searchResults.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center">
                    <h3 className="text-xl font-medium mb-2">No results found</h3>
                    <p className="text-muted-foreground">
                      We couldn't find any achievers matching "{initialQuery}"
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="py-10 text-center">
                <h3 className="text-xl font-medium mb-2">Enter a search term</h3>
                <p className="text-muted-foreground">
                  Use the search bar above to find achievers
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SearchResults;
